import React, { useState, useEffect, Suspense, lazy, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';

const Footer = lazy(() => import('../components/Footer'));
const Header = lazy(() => import('../components/Header'));

const HomePage = () => {
    const [stats, setStats] = useState(null);
    const [commands, setCommands] = useState([]);
    const [visibleCommands, setVisibleCommands] = useState([]);
    const [showAll, setShowAll] = useState(false);
    const [lookupQuery, setLookupQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const COMMANDS_THRESHOLD = 6;

    useEffect(() => {
        fetch("https://block.lenny.ie/stats", {
            method: 'GET',
        })
            .then((response) => response.json())
            .then((data) => {
                setStats(data);
                const combinedCommands = [
                    ...data.stats.commands.skyblock,
                    ...data.stats.commands.utility,
                    ...data.stats.commands.fun,
                ];
                setCommands(combinedCommands);
                setVisibleCommands(combinedCommands.slice(0, COMMANDS_THRESHOLD));
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching stats:", error);
                setLoading(false);
            });
    }, []);

    const handleViewMore = () => {
        setShowAll(!showAll);
        if (!showAll) {
            setVisibleCommands(commands);
            setTimeout(() => {
                document.getElementById('commands-section').scrollIntoView({ behavior: 'smooth' });
            }, 100);
        } else {
            setVisibleCommands(commands.slice(0, COMMANDS_THRESHOLD));
        }
    };

    const handleLookup = () => {
        if (lookupQuery.trim() !== '') {
            navigate(`/lookup?query=${encodeURIComponent(lookupQuery)}`);
        }
    };

    const handleInputChange = (event) => {
        setLookupQuery(event.target.value);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && lookupQuery.trim() !== '') {
            handleLookup();
        }
    };

    const capitalizeFirstLetter = (string) => {
        return string.charAt(0).toUpperCase() + string.slice(1);
    };

    const commandList = useMemo(() => {
        return visibleCommands.map((cmd, index) => (
            <div
                key={index}
                className="p-6 bg-secondary rounded-lg shadow-lg hover:shadow-xl transition-transform transform hover:scale-105"
            >
                <h4 className="text-lg font-semibold text-primary mb-2">
                    {capitalizeFirstLetter(cmd.name)}
                </h4>
                <p className="text-sm text-accent">{cmd.description}</p>
            </div>
        ));
    }, [visibleCommands]);

    return (
        <div className="flex flex-col min-h-screen bg-background text-text">
            <Suspense fallback={<div className="flex justify-center items-center py-4"><CircularProgress color="inherit" /></div>}>
                <Header />
            </Suspense>

            <main className="flex-grow">
                <section className="flex flex-col items-center justify-start pt-12 text-center">
                    <h1 className="text-5xl font-bold mb-4 text-primary">Welcome to Scammer List</h1>
                    <p className="text-xl mb-6 text-accent">Home to the Hypixel Skyblock Community and BlockHelper</p>
                </section>

                <section className="px-6 py-8">
                    {loading ? (
                        <div className="flex justify-center items-center h-40">
                            <CircularProgress color="inherit" size={50} />
                        </div>
                    ) : (
                        <>
                            <h2 className="text-xl text-center mb-2 text-accent">
                                With over <span className="font-bold text-primary">{stats?.stats?.scammerCount}</span> scammers added, be assured you're not being scammed...
                            </h2>
                            <h2 className="text-xl text-center mb-8 text-accent">
                                Trusted by over <span className="font-bold text-primary">{stats?.stats?.guildCount}</span> guilds and growing...
                            </h2>
                            <h2 className="text-4xl font-bold text-center mb-6 text-primary">Scammer Lookup</h2>
                            <div className="flex justify-center space-x-4 mb-6">
                                <input
                                    id="scammer-lookup-input"
                                    type="text"
                                    placeholder="Enter Scammer Name or Discord ID"
                                    className="px-4 py-3 border border-secondary rounded-lg w-full max-w-md bg-secondary text-accent placeholder-accent focus:outline-none focus:ring-2 focus:ring-primary"
                                    value={lookupQuery}
                                    onChange={handleInputChange}
                                    onKeyPress={handleKeyPress}
                                    aria-label="Enter Scammer Name or Discord ID"
                                />
                                <button
                                    className="px-6 py-3 bg-primary text-text rounded-lg hover:bg-opacity-90 transition-all ease-in-out duration-300"
                                    onClick={handleLookup}
                                    disabled={lookupQuery.trim() === ''}
                                    aria-label="Lookup Scammer"
                                >
                                    Lookup
                                </button>
                            </div>

                            <div className="flex justify-center space-x-4 mb-10">
                                <a
                                    href="https://discord.com/invite/scammerlist"
                                    className="px-6 py-3 bg-primary text-text rounded-lg hover:bg-opacity-90 transition-all ease-in-out duration-300"
                                    aria-label="Join Our Discord"
                                >
                                    Join Our Discord
                                </a>
                                <a
                                    href="https://discord.com/oauth2/authorize?client_id=1193165627704217710"
                                    className="px-6 py-3 bg-primary text-text rounded-lg hover:bg-opacity-90 transition-all ease-in-out duration-300"
                                    aria-label="Use the Bot"
                                >
                                    Use the Bot
                                </a>
                            </div>
                        </>
                    )}
                </section>

                <div id="commands-section" className="mt-6 mb-10 px-6 md:px-8 lg:px-16">
                    <h2 className="text-4xl font-bold text-center mb-6 text-primary">Commands</h2>
                    {loading ? (
                        <div className="flex justify-center items-center h-40">
                            <CircularProgress color="inherit" size={50} />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto transition-all duration-700 ease-in-out">
                            {commandList}
                        </div>
                    )}
                    {commands.length > COMMANDS_THRESHOLD && !loading && (
                        <div className="flex justify-center mt-8">
                            <button
                                className="px-6 py-3 bg-primary text-text rounded-lg hover:bg-opacity-90 transition-all duration-500 ease-in-out"
                                onClick={handleViewMore}
                                aria-label={showAll ? 'Show Less Commands' : 'Show More Commands'}
                            >
                                {showAll ? 'Show Less' : 'Show More'}
                            </button>
                        </div>
                    )}
                </div>
            </main>

            <Suspense fallback={<div className="flex justify-center items-center py-4"><CircularProgress color="inherit" /></div>}>
                <Footer />
            </Suspense>
        </div>
    );
};

export default HomePage;
